import { useContext, useRef, useState } from 'react';
import {
  Button,
  CircularProgress,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import BulkUploadContext from '../../services/bulkUploadService';
import {
  downloadBlobFile,
  resolveBulkUploadResultDownload,
  triggerFileDownload,
} from '../../services/bulkUploadDownloadUtils';
import { upsertUpload, useBulkUploadRow } from '../../stores/bulkUploadStore';

const DownloadBulkUploadResultsButton = ({
  uploadId,
  label = 'Download Results',
  fileSuffix = 'results',
  content = '',
  contentType,
  fileExtension,
  getContent,
  disabled = false,
  onError,
  buttonProps = {},
}) => {
  const theme = useTheme();
  const { getBulkUploadDetails } = useContext(BulkUploadContext);
  const uploadRow = useBulkUploadRow(uploadId);
  const linkRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (event) => {
    event.stopPropagation();

    if (downloading || disabled) {
      return;
    }

    setDownloading(true);
    try {
      if (fileSuffix !== 'results') {
        const resolvedContent = getContent ? await getContent() : content;
        const safeContent = resolvedContent ?? '';
        const resolvedType = contentType
          || (fileSuffix === 'errors' ? 'text/csv;charset=utf-8;' : 'text/plain;charset=utf-8;');
        const resolvedExtension = fileExtension || (resolvedType.includes('csv') ? 'csv' : 'txt');
        const fileName = uploadId
          ? `bulk-upload-${uploadId}-${fileSuffix}.${resolvedExtension}`
          : `bulk-upload-${fileSuffix}.${resolvedExtension}`;
        downloadBlobFile({
          content: safeContent,
          fileName,
          contentType: resolvedType,
          linkNode: linkRef.current,
        });
        return;
      }

      const { downloadUrl, fileName } = await resolveBulkUploadResultDownload({
        upload: uploadRow,
        uploadId,
        getBulkUploadDetails,
        onDetails: upsertUpload,
        fileNameFallback: `bulk-upload-${uploadId}-results.csv`,
        forceRefresh: true,
      });
      triggerFileDownload({
        href: downloadUrl,
        fileName,
        linkNode: linkRef.current,
      });
    } catch (error) {
      console.error('Unable to download bulk upload results:', error);
      if (onError) {
        onError('Unable to download results. Please try again.');
      }
    } finally {
      setDownloading(false);
    }
  };

  const {
    sx: buttonPropsSx,
    onClick: ignoredOnClick,
    disabled: ignoredDisabled,
    ...restButtonProps
  } = buttonProps;
  void ignoredOnClick;
  void ignoredDisabled;

  return (
    <>
      <a download ref={linkRef} onClick={(event) => event.stopPropagation()} hidden href="" />
      <Button
        variant="outlined"
        size="small"
        startIcon={downloading ? <CircularProgress size="1rem" /> : <Download />}
        {...restButtonProps}
        onClick={handleDownload}
        disabled={disabled || downloading}
        sx={{
          textTransform: 'none',
          color: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
          whiteSpace: 'nowrap',
          ':hover': {
            borderColor: theme.palette.primary.dark,
            backgroundColor: theme.palette.action.hover,
          },
          ...buttonPropsSx,
        }}
      >
        {label}
      </Button>
    </>
  );
};

export default DownloadBulkUploadResultsButton;
